import { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { ribbon, add } from 'ionicons/icons';
import { useCandidateAssociations, useDeleteMembership } from '@/lib/api/hooks';
import { MembershipCard } from '@/components/candidate/MembershipCard';
import { MembershipFormModal } from '@/components/candidate/MembershipFormModal';
import { MembershipVerificationStatus } from '@/components/candidate/MembershipVerificationStatus';
import type { ProfessionalAssociationMembership } from '@/types';
import { IonToast } from '@ionic/react';

interface ProfessionalMembershipsPanelProps {
  legalEntityId: string;
}

export function ProfessionalMembershipsPanel({ legalEntityId }: ProfessionalMembershipsPanelProps) {
  const { data: envelope, isLoading } = useCandidateAssociations(legalEntityId);
  const memberships = envelope?.data ?? [];
  const deleteMembership = useDeleteMembership();
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingMembership, setEditingMembership] = useState<ProfessionalAssociationMembership | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const handleEdit = (m: ProfessionalAssociationMembership) => {
    setEditingMembership(m);
    setFormOpen(true);
  };

  const handleAdd = () => {
    setEditingMembership(null);
    setFormOpen(true);
  };

  const handleDelete = (m: ProfessionalAssociationMembership) => {
    if (!m._id) return;
    deleteMembership.mutate(
      { id: m._id },
      {
        onSuccess: () => {
          setToast({ msg: 'Membership removed', color: 'success' });
          setConfirmDelete(null);
        },
        onError: (err: Error) => {
          setToast({ msg: err.message || 'Failed to delete', color: 'danger' });
        },
      },
    );
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingMembership(null);
  };

  if (isLoading) return null;

  return (
    <section className="bp-panel" style={{ borderLeft: '3px solid var(--ion-color-success)' }}>
      <div className="bp-panel-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IonIcon icon={ribbon} className="bp-panel-icon" />
          <h2 style={{ margin: 0 }}>Professional memberships</h2>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '4px 12px', borderRadius: 8,
            background: 'var(--bp-blue-500, #2563eb)',
            border: 'none', color: '#fff', fontSize: '0.75rem',
            cursor: 'pointer',
          }}
        >
          <IonIcon icon={add} style={{ fontSize: '0.85rem' }} />
          Add
        </button>
      </div>

      <div className="bp-panel-body">
        {memberships.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '24px 16px',
            color: 'var(--bp-text-muted, #64748b)', fontSize: '0.8rem',
          }}>
            <p style={{ margin: '0 0 8px' }}>No memberships added yet</p>
            <p style={{ margin: 0 }}>Add your professional association memberships to build trust with employers.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {memberships.map((m) => (
              <div key={m._id}>
                <MembershipCard
                  membership={m}
                  onEdit={handleEdit}
                  onDelete={(mem) => setConfirmDelete(mem._id ?? null)}
                />
                <MembershipVerificationStatus membership={m} />
                {confirmDelete === m._id && (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    gap: 8, padding: '8px 0',
                  }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted, #64748b)' }}>
                      Remove this membership?
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDelete(m)}
                      disabled={deleteMembership.isPending}
                      style={{
                        padding: '4px 12px', borderRadius: 6, fontSize: '0.75rem',
                        background: 'var(--bp-error, #ef4444)', border: 'none',
                        color: '#fff', cursor: 'pointer',
                      }}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(null)}
                      style={{
                        padding: '4px 12px', borderRadius: 6, fontSize: '0.75rem',
                        background: 'rgba(255,255,255,0.05)', border: 'none',
                        color: 'var(--bp-text-muted, #64748b)', cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <MembershipFormModal
        open={formOpen}
        onClose={handleCloseForm}
        membership={editingMembership}
        legalEntityId={legalEntityId}
      />

      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
    </section>
  );
}
